import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Check, X, Coins, Clock, BadgeCheck, XCircle } from "lucide-react";
import { toast } from "sonner";

const BADGE = {
  awaiting_review: { label: "Pending", cls: "text-amber-300 bg-amber-300/10 border-amber-300/30", Icon: Clock },
  paid: { label: "Credited", cls: "text-emerald-300 bg-emerald-300/10 border-emerald-300/30", Icon: BadgeCheck },
  rejected: { label: "Rejected", cls: "text-red-300 bg-red-300/10 border-red-300/30", Icon: XCircle },
};

export default function AdminVenmo() {
  const [busy, setBusy] = useState(null);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-venmo"],
    queryFn: async () => (await api.get("/admin/venmo-requests")).data,
    refetchInterval: 15000,
  });

  const act = async (order_id, kind) => {
    setBusy(order_id + kind);
    try {
      await api.post(`/admin/venmo-requests/${order_id}/${kind}`);
      toast.success(kind === "approve" ? "Credits added" : "Request rejected");
      refetch();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(null);
    }
  };

  const rows = data || [];
  const pending = rows.filter((r) => r.status === "pending");

  return (
    <div data-testid="admin-venmo">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl text-white tracking-[0.04em]">Venmo Top-Ups</h1>
          <p className="text-white/50 text-sm">Verify the payment in Venmo, then approve to add credits.</p>
        </div>
        <span className="font-mono text-[11px] text-amber-300 bg-amber-300/10 border border-amber-300/30 rounded-full px-3 py-1">
          {pending.length} pending
        </span>
      </div>

      {isLoading ? (
        <div className="text-white/40 py-12 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="hud-pill rounded-2xl p-10 text-center text-white/45">No Venmo requests yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const b = BADGE[r.payment_status] || BADGE.awaiting_review;
            return (
              <div key={r.order_id} data-testid={`admin-venmo-${r.order_id}`} className="hud-pill rounded-xl p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-white font-medium flex items-center gap-2">
                    <Coins className="h-4 w-4 text-amber-300" /> {(r.credits || 0).toLocaleString()} credits
                    <span className="text-white/45 font-mono text-sm">· ${Number(r.amount_usd || 0).toFixed(2)}</span>
                  </div>
                  <div className="font-mono text-[11px] text-white/45 mt-0.5">
                    {r.user_email} · ref <span className="text-amber-200">{r.ref_code}</span>
                    {r.payer_handle ? <> · paid as <span className="text-[#3D95CE]">@{r.payer_handle}</span></> : null}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 font-mono text-[10px] rounded-full border px-2 py-0.5 ${b.cls}`}>
                  <b.Icon className="h-3 w-3" /> {b.label}
                </span>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button
                      data-testid={`venmo-approve-${r.order_id}`}
                      onClick={() => act(r.order_id, "approve")}
                      disabled={busy === r.order_id + "approve"}
                      className="bg-emerald-400 text-black hover:bg-emerald-300 h-9"
                    >
                      {busy === r.order_id + "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Approve</>}
                    </Button>
                    <Button
                      data-testid={`venmo-reject-${r.order_id}`}
                      onClick={() => act(r.order_id, "reject")}
                      disabled={busy === r.order_id + "reject"}
                      variant="ghost"
                      className="text-red-300 hover:bg-red-500/10 h-9"
                    >
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
